/// <amd-module name="Wit/Common/WorkItemControls/IterationDropdown" />

import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IReadonlyObservableArray, ObservableArray, ObservableLike, ObservableValue, useObservableArray } from "VSS/Core/Observable";
import { useComponentContext } from "VSS/Platform/Layout";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { IListBoxItem, wrapListBoxItems } from "VSSUI/ListBox";
import { SimpleTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ITreeColumn } from "VSSUI/TreeEx";
import { css } from "VSSUI/Util";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getDateInUTC } from "Wit/Common/DateUtils";
import { INodeTreeDropdownProps, NodeTreeDropdown } from "Wit/Common/WorkItemControls/NodeTreeDropdown";
import { INodeTreeItem } from "Wit/Common/WorkItemControls/NodeTreeItemProvider";
import { convertNodesToListBoxItems, isEmpty } from "Wit/Common/WorkItemControls/NodeUtils";
import { IWorkItemNodeService } from "Wit/Common/WorkItemNodeService/IWorkItemNodeService";

export interface IIterationDropdownProps extends Omit<INodeTreeDropdownProps, "items"> {
    /**
     * Items should be supplied as an ObservableArray if they will be changing.
     */
    items?: IReadonlyObservableArray<IListBoxItem<INodeTreeItem>> | IListBoxItem<INodeTreeItem>[];
    /**
     * List of macros that will be displayed at the top of the tree
     * NOTE: if an itemProvider is passed, this list will be ignored. Parent component should manage macros in that case.
     */
    macros?: string[] | IListBoxItem[];
    /**
     * Optional project id, will default to current project if iteration data needs to be loaded
     */
    projectId?: string;
}

export const IterationDropdown = React.forwardRef<EditableDropdown<INodeTreeItem>, IIterationDropdownProps>((props, ref) => {
    const { items, macros, onValueChange, projectId, ...rest } = props;
    const { pageContext } = useComponentContext();
    const pageData = getTFSPageData(pageContext);
    const nodeRef = React.useRef<IListBoxItem<WorkItemClassificationNode>[]>([]);
    const wrappedMacros = macros && (wrapListBoxItems(macros) || macros);
    const wrappedItems = ObservableLike.isObservable(items)
        ? (items as ObservableArray<IListBoxItem<WorkItemClassificationNode>>)
        : new ObservableArray((items as IListBoxItem<WorkItemClassificationNode>[]) ?? wrappedMacros);

    const [mruNodes] = useObservableArray<number>([]);

    const wrappedItemsRef = React.useRef<ObservableArray<IListBoxItem<WorkItemClassificationNode>>>(new ObservableArray(wrappedItems.value.concat(nodeRef.current)));

    React.useEffect(() => {
        wrappedItemsRef.current.value = wrappedItems.value.concat(nodeRef.current);
    }, [wrappedItems.length])

    React.useEffect(() => {
        const loadIterations = async () => {
            const projectToLoad = projectId ?? pageData?.project?.id;
            if (!items && projectToLoad) {
                const nodeService = pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
                const node = await nodeService.getIterationNodes(projectToLoad);

                if (node) {
                    nodeRef.current = convertNodesToListBoxItems(node);
                    wrappedItemsRef.current.value = wrappedItems.value.concat(nodeRef.current);
                }
            }
        };

        const loadMruData = async () => {
            const projectToLoad = projectId ?? pageData?.project?.id;
            if (projectToLoad) {
                const nodeService = pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
                mruNodes.value = await nodeService.getMruIterations(projectToLoad);
            }
        };

        if (!items) {
            loadIterations();
        }

        loadMruData();
    }, [projectId]);

    return (
        <NodeTreeDropdown
            allowFreeform={!!macros}
            additionalColumns={columns}
            items={items ? wrappedItems : wrappedItemsRef.current}
            mruNodes={mruNodes}
            onValueChange={async value => {
                if (onValueChange) {
                    onValueChange(value);
                }

                const project = projectId ?? pageData?.project?.id;
                if (!project || isEmpty(value?.data)) {
                    return;
                }

                const node = value!.data as WorkItemClassificationNode;
                const nodeService = pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
                mruNodes.value = await nodeService.updateMruIterations(project, [node.id]);
            }}
            ref={ref}
            {...rest}
        />
    );
});

const columns = [
    {
        id: "iteration-dates",
        width: new ObservableValue(-50),
        renderCell: renderIterationDatesColumn
    }
];

const dateFormatter = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "numeric", day: "numeric" });

function renderIterationDatesColumn(
    rowIndex: number,
    columnIndex: number,
    treeColumn: ITreeColumn<IListBoxItem<INodeTreeItem>>,
    treeItem: ITreeItemEx<IListBoxItem<INodeTreeItem>>
): JSX.Element {
    const attributes = (treeItem.underlyingItem.data.data as WorkItemClassificationNode)?.attributes;
    const key = `iteration-dates-cell-${rowIndex}-${columnIndex}`;
    let content: JSX.Element | null = null;
    if (attributes) {
        const startDate = attributes["startDate"] as Date;
        const finishDate = attributes["finishDate"] as Date;
        if (startDate && finishDate) {
            const innerContent = `${dateFormatter.format(getDateInUTC(startDate))} - ${dateFormatter.format(getDateInUTC(finishDate))}`;
            content = (
                <Tooltip overflowOnly>
                    <span className="text-ellipsis">{innerContent}</span>
                </Tooltip>
            );
        }
    }

    return (
        <SimpleTableCell
            className={css(treeColumn.className, treeItem.className)}
            columnIndex={columnIndex}
            contentClassName="justify-end"
            key={key}
            tableColumn={treeColumn}
        >
            {content}
        </SimpleTableCell>
    );
}
